import React, { useEffect, useMemo, useState } from "react";
import { API, authHeaders } from "../services/config";
import { EMPTY_HISTORIA_FORM, fetchHistoriaClinicaFormData } from "../services/historiaClinica";
import "./HistoriaClinicaImpresion.css";

// ── Colores idénticos al Odontograma de la app ───────────────────────────────
const C = {
  extraccion: "#1d4ed8",
  ausente:    "#D32F2F",
  corona:     "#7c3aed",
  bridge:     "#d97706",
  caries:     "#64B5F6",
  restaurado: "#ef4444",
  endodoncia: "#f97316",
  implante:   "#6366f1",
  sellador:   "#eab308",
  fractura:   "#374151",
  otro:       "#BDBDBD",
};

const FACES_ODO = ["oclusal", "mesial", "distal", "vestibular", "lingual"];

// Filas del odontograma (para conteos y renderizado)
const ROWS_ODO = [
  [18, 17, 16, 15, 14, 13, 12, 11],
  [21, 22, 23, 24, 25, 26, 27, 28],
  [55, 54, 53, 52, 51],
  [61, 62, 63, 64, 65],
  [48, 47, 46, 45, 44, 43, 42, 41],
  [31, 32, 33, 34, 35, 36, 37, 38],
  [85, 84, 83, 82, 81],
  [71, 72, 73, 74, 75],
];

const TOOTH_NUMBERS = ROWS_ODO.flat();

function createOdoEmpty() {
  return TOOTH_NUMBERS.reduce((acc, n) => {
    acc[n] = {
      vestibular: null, mesial: null, oclusal: null, distal: null, lingual: null,
      cross: null, circle: null, bridge: null,
    };
    return acc;
  }, {});
}

function mapApiToOdo(piezas) {
  const next = createOdoEmpty();
  (piezas || []).forEach((row) => {
    if (!next[row.pieza]) return;
    if (row.marca === "cross")          next[row.pieza].cross  = row.color;
    if (row.marca === "circle")         next[row.pieza].circle = row.color;
    if (row.marca === "bridge_pilar")   next[row.pieza].bridge = "pilar";
    if (row.marca === "bridge_pontico") next[row.pieza].bridge = "pontico";
    if (FACES_ODO.includes(row.cara))   next[row.pieza][row.cara] = row.color;
  });
  return next;
}

// ── Conteos para la tabla resumen ────────────────────────────────────────────
function computeCounts(odonto) {
  // Prótesis fija = grupos contiguos de dientes con bridge
  let bridgeGroups = 0;
  ROWS_ODO.forEach((row) => {
    let inBridge = false;
    row.forEach((n) => {
      const has = odonto[n]?.bridge !== null;
      if (has && !inBridge) { bridgeGroups++; inBridge = true; }
      if (!has)              { inBridge = false; }
    });
  });

  const coronas  = TOOTH_NUMBERS.filter(n => odonto[n]?.circle !== null).length;
  const ausentes = TOOTH_NUMBERS.filter(n => odonto[n]?.cross === C.ausente).length;
  const ponticos = TOOTH_NUMBERS.filter(n => odonto[n]?.bridge === "pontico").length;
  // Existentes = todos los dientes menos los ausentes y los pónticos (artificiales)
  const existentes = TOOTH_NUMBERS.length - ausentes - ponticos;

  return { bridgeGroups, coronas, ausentes, existentes };
}

// ── Casillas SI / NO ─────────────────────────────────────────────────────────
function SiNo({ val }) {
  return (
    <span className="hci-sino">
      <span className={`hci-sino-box${val === "SI" ? " checked" : ""}`}>SI</span>
      <span className={`hci-sino-box${val === "NO" ? " checked" : ""}`}>NO</span>
    </span>
  );
}

// ── Fila del cuestionario (dentro del bloque 2 columnas) ─────────────────────
function QR({ label, sm = false, children }) {
  return (
    <div className="hci-qrow">
      <span className={sm ? "hci-lbl-sm" : "hci-lbl"}>{label}</span>
      {children}
    </div>
  );
}

// ── Valor con línea de subrayado ─────────────────────────────────────────────
function L({ val }) {
  return <span className="hci-val hci-qline">{val}</span>;
}

// ── Diente: SVG inline ───────────────────────────────────────────────────────
// SVG renderiza idéntico en pantalla y en PDF — evita problemas de CSS Grid
// en motores de impresión.
//
// Distribución de las 5 caras (S = tamaño total, B = ancho de franja ~30 %):
//
//   ┌────┬──────────┬────┐
//   │gap │vestibular│gap │  fila 1
//   ├────┼──────────┼────┤
//   │mes │  oclusal │dis │  fila 2
//   ├────┼──────────┼────┤
//   │gap │ lingual  │gap │  fila 3
//   └────┴──────────┴────┘
//
function ToothSVG({ data, size = 20 }) {
  const s  = size;
  const b  = Math.round(s * 0.30); // ancho de franjas laterales
  const ci = s / 2;
  const gap = "#b8c4ce"; // esquinas vacías

  const fc = (c) => c || "#f0f0f0"; // color de cara o gris claro por defecto

  return (
    <svg
      width={s}
      height={s}
      viewBox={`0 0 ${s} ${s}`}
      style={{ display: "block", flexShrink: 0 }}
      aria-hidden="true"
    >
      {/* Fondo (esquinas/gap) */}
      <rect width={s} height={s} fill={gap} />

      {/* Vestibular — franja superior central */}
      <rect x={b}   y={0}   width={s - 2 * b} height={b}         fill={fc(data.vestibular)} />
      {/* Lingual   — franja inferior central */}
      <rect x={b}   y={s-b} width={s - 2 * b} height={b}         fill={fc(data.lingual)}    />
      {/* Mesial    — franja izquierda central */}
      <rect x={0}   y={b}   width={b}          height={s - 2 * b} fill={fc(data.mesial)}     />
      {/* Distal    — franja derecha central */}
      <rect x={s-b} y={b}   width={b}          height={s - 2 * b} fill={fc(data.distal)}     />
      {/* Oclusal   — cuadrado central */}
      <rect x={b}   y={b}   width={s - 2 * b} height={s - 2 * b} fill={fc(data.oclusal)}    />

      {/* Póntico: capa ámbar semi-transparente */}
      {data.bridge === "pontico" && (
        <rect width={s} height={s} fill="#FFE0B2" opacity="0.55" />
      )}

      {/* Corona: círculo sólido */}
      {data.circle && (
        <circle cx={ci} cy={ci} r={ci - 1.5}
          fill="none" stroke={data.circle} strokeWidth="1.5" />
      )}

      {/* Pilar: círculo punteado ámbar */}
      {data.bridge === "pilar" && (
        <circle cx={ci} cy={ci} r={ci - 1.5}
          fill="none" stroke="#d97706" strokeWidth="1.5" strokeDasharray="2 1.5" />
      )}

      {/* Cruz: extracción (azul) / ausente (rojo) */}
      {data.cross && (
        <text
          x={ci} y={ci + s * 0.27}
          textAnchor="middle"
          fontSize={s * 0.82}
          fontWeight="900"
          fill={data.cross}
          style={{ fontFamily: "Arial, sans-serif" }}
        >✕</text>
      )}

      {/* Borde exterior */}
      <rect x={0.5} y={0.5} width={s - 1} height={s - 1}
        fill="none" stroke="#6b7280" strokeWidth="1" rx="1.5" />
    </svg>
  );
}

function ToothPrint({ num, data, bridgeLeft = false, bridgeRight = false }) {
  const hasBridge = data.bridge !== null;
  return (
    <div className="hci-tooth">
      <span className="hci-tooth-num">{num}</span>
      <div className="hci-tooth-wrap">
        {hasBridge && (
          <div className={`hci-bbar hci-bbar--t${bridgeLeft ? " hci-bbar--el" : ""}${bridgeRight ? " hci-bbar--er" : ""}`} />
        )}
        <ToothSVG data={data} size={20} />
        {hasBridge && (
          <div className={`hci-bbar hci-bbar--b${bridgeLeft ? " hci-bbar--el" : ""}${bridgeRight ? " hci-bbar--er" : ""}`} />
        )}
      </div>
    </div>
  );
}

function ToothRow({ nums, odonto }) {
  return (
    <div className="hci-tooth-row">
      {nums.map((n, i) => (
        <ToothPrint
          key={n}
          num={n}
          data={odonto[n]}
          bridgeLeft={i > 0               && !!odonto[nums[i - 1]]?.bridge}
          bridgeRight={i < nums.length - 1 && !!odonto[nums[i + 1]]?.bridge}
        />
      ))}
    </div>
  );
}

// ── Componente principal ─────────────────────────────────────────────────────
export default function HistoriaClinicaImpresion({ pacienteId, formData: formDataProp, onClose }) {
  const [formData,   setFormData]   = useState(formDataProp || EMPTY_HISTORIA_FORM);
  const [loadingHc,  setLoadingHc]  = useState(!formDataProp);
  const [loadError,  setLoadError]  = useState(null);
  const [odonto,     setOdonto]     = useState(createOdoEmpty);
  const [odObs,      setOdObs]      = useState("");
  const [loadingOdo, setLoadingOdo] = useState(true);

  useEffect(() => {
    if (formDataProp) {
      setFormData(formDataProp);
      setLoadingHc(false);
      setLoadError(null);
      return undefined;
    }
    if (!pacienteId) { setLoadingHc(false); return undefined; }
    let cancelled = false;
    setLoadingHc(true);
    fetchHistoriaClinicaFormData(pacienteId)
      .then(data  => { if (!cancelled) setFormData(data); })
      .catch(err  => { if (!cancelled) setLoadError(err.message || "Error al cargar"); })
      .finally(() => { if (!cancelled) setLoadingHc(false); });
    return () => { cancelled = true; };
  }, [pacienteId, formDataProp]);

  useEffect(() => {
    if (!pacienteId) { setLoadingOdo(false); return undefined; }
    let cancelled = false;
    fetch(`${API}/pacientes/${pacienteId}/odontograma`, { headers: authHeaders() })
      .then(r => r.json())
      .then(data => {
        if (cancelled) return;
        if (data.success && data.data?.piezas) {
          setOdonto(mapApiToOdo(data.data.piezas));
          setOdObs(data.data?.odontograma?.observaciones || "");
        }
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoadingOdo(false); });
    return () => { cancelled = true; };
  }, [pacienteId]);

  const f       = formData;
  const loading = loadingHc || loadingOdo;

  // Conteos para la tabla resumen (se recalcula sólo cuando cambia odonto)
  const counts = useMemo(() => computeCounts(odonto), [odonto]);

  return (
    <div className="hci-overlay" role="dialog" aria-modal="true" aria-label="Vista previa Historia Clínica">
      <div className="hci-modal">

        {/* ── Barra de herramientas ──────────────────────────────────────── */}
        <div className="hci-toolbar no-print">
          <span className="hci-toolbar-title">Vista previa · Historia Clínica General</span>
          <div className="hci-toolbar-actions">
            <button type="button" className="boton-secundario" onClick={onClose}>Cerrar</button>
            <button type="button" className="pp-btn-guardar hci-print-btn" onClick={() => window.print()}>
              Imprimir / Guardar PDF
            </button>
          </div>
        </div>

        {loading && <div className="hci-loading no-print">Cargando historia clínica…</div>}

        {loadError && (
          <div className="hci-load-error no-print">
            {loadError}
            <button type="button" className="boton-secundario" onClick={onClose}>Cerrar</button>
          </div>
        )}

        {!loadingHc && !loadError && (
          <div className="hci-document">
            <div className="hci-page">

              {/* ══════════════════════════════════════════════
                  ENCABEZADO
              ══════════════════════════════════════════════ */}
              <div className="hci-enc">
                <h1 className="hci-enc-titulo">HISTORIA CLÍNICA GENERAL</h1>
                <div className="hci-enc-row">
                  <span>Lugar:&nbsp;<span className="hci-val">{f.lugar}</span></span>
                  <span>Fecha:&nbsp;<span className="hci-val">{f.fecha}</span></span>
                </div>
                <div className="hci-enc-row">
                  <span>ODONTÓLOGO:&nbsp;<span className="hci-val">{f.odontologo}</span></span>
                  <span>N° de Matrícula:&nbsp;<span className="hci-val">{f.matricula}</span></span>
                </div>
                <div className="hci-enc-row">
                  <span>PACIENTE:&nbsp;<span className="hci-val hci-val--bold">{f.paciente}</span></span>
                  <span>O. Social:&nbsp;<span className="hci-val">{f.obraSocial}</span></span>
                  <span>F.Nac.:&nbsp;<span className="hci-val">{f.fechaNac}</span></span>
                  <span>Tel.:&nbsp;<span className="hci-val">{f.tel}</span></span>
                </div>
                <div className="hci-enc-row">
                  <span>Edad:&nbsp;<span className="hci-val">{f.edad}</span></span>
                  <span>Estado Civil:&nbsp;<span className="hci-val">{f.estadoCivil}</span></span>
                  <span>Nacionalidad:&nbsp;<span className="hci-val">{f.nacionalidad}</span></span>
                  <span>N° Doc.:&nbsp;<span className="hci-val">{f.dni}</span></span>
                  <span>Cel.:&nbsp;<span className="hci-val">{f.cel}</span></span>
                </div>
                <div className="hci-enc-row">
                  <span>Domicilio:&nbsp;<span className="hci-val hci-val--wide">{f.domicilio}</span></span>
                </div>
                <div className="hci-enc-row">
                  <span>Profesión:&nbsp;<span className="hci-val">{f.profesion}</span></span>
                  <span>Titular:&nbsp;<span className="hci-val">{f.titular}</span></span>
                  <span>Lugar de trabajo:&nbsp;<span className="hci-val">{f.trabajo}</span></span>
                  <span>Jerarquía:&nbsp;<span className="hci-val">{f.jerarquia}</span></span>
                </div>
              </div>

              <div className="hci-dj-banner">
                Este cuestionario tiene el tenor de una <em>"Declaración Jurada"</em>
              </div>

              {/* ══════════════════════════════════════════════
                  BLOQUE 2 COLUMNAS
                  Antecedentes → HC Odontológica →
                  luego Declaración + HR con column-span:all
              ══════════════════════════════════════════════ */}
              <div className="hci-two-col">

                {/* ── ANTECEDENTES MÉDICOS ── */}
                <QR label="Padre con vida?"><SiNo val={f.padreVive} /></QR>
                <QR label="enfermedad que padece o padeció" sm><L val={f.enfPadre} /></QR>
                <QR label="Madre con vida?"><SiNo val={f.madreVive} /></QR>
                <QR label="enfermedad que padece o padeció" sm><L val={f.enfMadre} /></QR>
                <QR label="Hermanos?"><SiNo val={f.hermanos} /></QR>
                <QR label="¿sanos?" sm><L val={f.hermanosSanos} /></QR>
                <QR label="¿Sufre de alguna enfermedad?"><SiNo val={f.enfermedad} /></QR>
                <QR label="¿de qué?" sm><L val={f.enfermedadDetalle} /></QR>
                <QR label="¿Hace algún tratamiento médico?"><SiNo val={f.tratamientoMedico} /></QR>
                <QR label="¿cuál?" sm><L val={f.tratamientoDetalle} /></QR>
                <QR label="¿Qué medicamento/s consume habitualmente?"><L val={f.medicamentosHabituales} /></QR>
                <QR label="¿Qué medicamentos consumió en los últimos 5 años?"><L val={f.medicamentosUltimos5} /></QR>
                <QR label="¿Realiza algún deporte?"><SiNo val={f.deporte} /></QR>
                <QR label="¿Nota algún malestar al realizarlo?" sm><SiNo val={f.malestarDeporte} /></QR>
                <QR label="¿Es alérgico a alguna droga?"><SiNo val={f.alergico} /></QR>
                <QR label="a la anestesia / penicilina / otros:" sm><L val={f.alergiasDetalle} /></QR>
                <QR label="Cuando le sacan una muela o se lastima, ¿cicatriza bien? ¿sangra mucho?"><L val={f.cicatrizacion} /></QR>
                <QR label="¿Tiene problema de colágeno (hiperlaxitud)?"><SiNo val={f.colageno} /></QR>
                <QR label="¿Antecedentes de fiebre reumática?"><SiNo val={f.fiebreReumatica} /></QR>
                <QR label="¿Se protege con alguna medicación?" sm><L val={f.medicacionProteccion} /></QR>
                <QR label="¿Es diabético?"><SiNo val={f.diabetico} /></QR>
                <QR label="¿está controlado? ¿con qué?" sm><L val={f.controlDiabetes} /></QR>
                <QR label="¿Tiene algún problema cardíaco?"><SiNo val={f.problemaCardiaco} /></QR>
                <QR label="¿cuál?" sm><L val={f.cualCardiaco} /></QR>
                <QR label="¿Toma seguido aspirina y/o anticoagulante?"><SiNo val={f.aspirinaAnticoagulante} /></QR>
                <QR label="¿con qué frecuencia?" sm><L val={f.frecuenciaAnticoagulante} /></QR>
                <QR label="¿Tiene presión alta?"><SiNo val={f.presionAlta} /></QR>
                <QR label="¿Chagas?"><SiNo val={f.chagas} /></QR>
                <QR label="¿está en tratamiento?" sm><L val={f.tratamientoChagas} /></QR>
                <QR label="¿Tiene problemas renales?"><SiNo val={f.problemaRenal} /></QR>
                <QR label="¿Úlcera Gástrica?"><SiNo val={f.ulcera} /></QR>
                <QR label="¿Tuvo hepatitis?"><SiNo val={f.hepatitis} /></QR>
                <QR label="¿de qué tipo? A · B · C" sm><L val={f.tipoHepatitis} /></QR>
                <QR label="¿Tiene algún problema hepático?"><SiNo val={f.problemaHepatico} /></QR>
                <QR label="¿cuál?" sm><L val={f.cualHepatico} /></QR>
                <QR label="¿Tuvo convulsiones?"><SiNo val={f.convulsiones} /></QR>
                <QR label="¿Es epiléptico?"><SiNo val={f.epilepsia} /></QR>
                <QR label="medicación que toma:" sm><L val={f.medicacionEpilepsia} /></QR>
                <QR label="¿Ha tenido Sífilis o Gonorrea?"><SiNo val={f.sifilis} /></QR>
                <QR label="¿Otra enfermedad infecto-contagiosa?"><SiNo val={f.infectoContagiosa} /></QR>
                <QR label="¿Tuvo transfusiones?"><SiNo val={f.transfusiones} /></QR>
                <QR label="¿Fue operado alguna vez?"><SiNo val={f.operado} /></QR>
                <QR label="¿de qué?" sm><L val={f.deQueOperacion} /></QR>
                <QR label="¿Cuándo?" sm><L val={f.cuandoOperacion} /></QR>
                <QR label="¿Tiene algún problema respiratorio?"><SiNo val={f.problemaRespiratorio} /></QR>
                <QR label="¿cuál?" sm><L val={f.cualRespiratorio} /></QR>
                <QR label="¿Fuma?"><SiNo val={f.fuma} /></QR>
                <QR label="¿Está embarazada?"><SiNo val={f.embarazada} /></QR>
                <QR label="¿de cuántos meses?" sm><L val={f.mesesEmbarazo} /></QR>
                <QR label="¿Otra enfermedad o recomendación de su médico?"><L val={f.otrasEnfermedades} /></QR>
                <QR label="¿Tratamiento homeopático, Acupuntura u otros?"><L val={f.tratamientosAlternativos} /></QR>
                <QR label="Médico clínico:"><L val={f.medicoClinico} /></QR>
                <QR label="Clínica/Hospital en caso de hacer falta derivación:"><L val={f.clinicaDerivacion} /></QR>

                {/* ── HISTORIA CLÍNICA ODONTOLÓGICA ── */}
                <h3 className="hci-col-sec-h">Historia Clínica Odontológica</h3>

                <QR label="¿Por qué asistió a la consulta?"><L val={f.motivoConsulta} /></QR>
                <QR label="¿Consultó antes con algún otro profesional?"><SiNo val={f.otroProfesional} /></QR>
                <QR label="¿Tomó algún medicamento?"><SiNo val={f.tomoMedicamento} /></QR>
                <QR label="Nombre de los medicamentos:" sm><L val={f.medicamentosConsulta} /></QR>
                <QR label="¿Desde cuándo?" sm><L val={f.desdeCuando} /></QR>
                <QR label="¿Obtuvo resultados?"><SiNo val={f.resultados} /></QR>
                <QR label="¿Ha tenido dolor? ¿de qué tipo?"><L val={f.tipoDolor} /></QR>
                <QR label="Localizado ¿dónde?" sm><L val={f.localizadoDonde} /></QR>
                <QR label="Irradiado ¿hacia dónde?" sm><L val={f.irradiadoHacia} /></QR>
                <QR label="¿Puede calmarlo con algo?"><L val={f.calmaCon} /></QR>
                <QR label="¿Sufrió algún golpe en los dientes?"><SiNo val={f.golpeDientes} /></QR>
                <QR label="¿cuándo? ¿cómo se produjo?" sm><L val={`${f.cuandoGolpe} ${f.comoGolpe}`.trim()} /></QR>
                <QR label="¿Se le fracturó algún diente?"><SiNo val={f.fracturaDiente} /></QR>
                <QR label="¿cuál? ¿recibió algún tratamiento?" sm><L val={`${f.cualDiente} ${f.tratamientoFractura}`.trim()} /></QR>
                <QR label="¿Tiene dificultad para hablar?"><L val={f.dificultadHablar} /></QR>
                <QR label="¿para masticar?" sm><L val={f.dificultadMasticar} /></QR>
                <QR label="¿para abrir la boca?" sm><L val={f.dificultadAbrirBoca} /></QR>
                <QR label="¿para tragar los alimentos?" sm><L val={f.dificultadTragar} /></QR>
                <QR label="¿Ha observado algo anormal en los labios? ¿lengua? ¿paladar? ¿piso de boca? ¿carrillos? ¿rebordes? ¿trígono? ¿retromolar?" />
                <QR label="¿Manchas?"><SiNo val={f.manchas} /></QR>
                <QR label="¿Abultamiento de los tejidos?"><SiNo val={f.abultamientos} /></QR>
                <QR label="¿Ulceraciones?"><SiNo val={f.ulceraciones} /></QR>
                <QR label="¿Ampollas?"><SiNo val={f.ampollas} /></QR>
                <QR label="Otros:" sm><L val={f.otrosLesiones} /></QR>
                <QR label="¿Le sangran las encías?"><SiNo val={f.sangradoEncías} /></QR>
                <QR label="¿cuándo?" sm><L val={f.cuandoSangrado} /></QR>
                <QR label="¿Sale pus de algún lugar de su boca?"><SiNo val={f.pus} /></QR>
                <QR label="¿de dónde?" sm><L val={f.deDondePus} /></QR>
                <QR label="¿Tiene movilidad en sus dientes?"><SiNo val={f.movilidadDientes} /></QR>
                <QR label="¿al morder siente altos los dientes?" sm><L val={f.dientesAltos} /></QR>
                <QR label="¿Ha tenido la cara hinchada?"><SiNo val={f.caraHinchada} /></QR>
                <QR label="¿Se puso hielo? ¿calor? ¿otros?" sm><L val={f.tratamientoCara} /></QR>
                <QR label="Momentos de azúcar diario:"><L val={f.azucarDiario} /></QR>
                <QR label="Índice de placa:" sm><L val={f.indicePlaca} /></QR>
                <QR label="Estado de la higiene bucal:"><L val={f.higieneBucal} /></QR>
                <div className="hci-qrow">
                  <span className="hci-lbl">Estado bucal general — Sarro:</span>
                  <SiNo val={f.sarro} />
                  <span className="hci-lbl-sm">Enf. Periodontal:</span>
                  <SiNo val={f.enfermedadPeriodontal} />
                </div>

                {/* ── Declaración: ocupa el ancho completo (column-span: all) ── */}
                <p className="hci-declaracion">
                  Declaro que he contestado todas las preguntas con honestidad y según mi conocimiento.
                  Asimismo, he sido informado que los datos suministrados quedan reservados en la presente
                  Historia Clínica y amparados en secreto profesional.
                </p>
                <div className="hci-hr" />

              </div>{/* ── fin hci-two-col ── */}

              {/* ══════════════════════════════════════════════
                  ODONTOGRAMA
              ══════════════════════════════════════════════ */}
              <div className="hci-odo-section">
                <div className="hci-sec-h">Odontograma</div>

                {/* ── Leyenda + Tabla de conteos ── */}
                <div className="hci-odo-ley-wrap">
                  {/* Leyenda de colores (igual que la app) */}
                  <div className="hci-odo-leyenda">
                    <div className="hci-ley-item">
                      <span className="hci-ley-x" style={{ color: C.extraccion }}>✕</span>
                      <span>Extracción a realizar</span>
                    </div>
                    <div className="hci-ley-item">
                      <span className="hci-ley-x" style={{ color: C.ausente }}>✕</span>
                      <span>Diente ausente</span>
                    </div>
                    <div className="hci-ley-item">
                      <span className="hci-ley-circle" style={{ borderColor: C.corona }} />
                      <span>Corona</span>
                    </div>
                    <div className="hci-ley-item">
                      <span className="hci-ley-circle hci-ley-circle--dashed" style={{ borderColor: C.bridge }} />
                      <span>Pilar de puente</span>
                    </div>
                    <div className="hci-ley-item">
                      <span className="hci-ley-bar" style={{ background: C.bridge }} />
                      <span>Puente (pilar / póntico)</span>
                    </div>
                    <div className="hci-ley-item">
                      <span className="hci-ley-dot" style={{ background: C.caries }} />
                      <span>Caries</span>
                    </div>
                    <div className="hci-ley-item">
                      <span className="hci-ley-dot" style={{ background: C.restaurado }} />
                      <span>Restauración</span>
                    </div>
                    <div className="hci-ley-item">
                      <span className="hci-ley-dot" style={{ background: C.endodoncia }} />
                      <span>Endodoncia</span>
                    </div>
                    <div className="hci-ley-item">
                      <span className="hci-ley-dot" style={{ background: C.implante }} />
                      <span>Implante</span>
                    </div>
                    <div className="hci-ley-item">
                      <span className="hci-ley-dot" style={{ background: C.sellador }} />
                      <span>Sellador</span>
                    </div>
                    <div className="hci-ley-item">
                      <span className="hci-ley-dot" style={{ background: C.fractura }} />
                      <span>Fractura</span>
                    </div>
                  </div>

                  {/* Tabla resumen de conteos */}
                  <table className="hci-odo-counts">
                    <thead>
                      <tr>
                        <th>Ítem</th>
                        <th>Cant.</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>Prótesis fija</td>
                        <td>{counts.bridgeGroups}</td>
                      </tr>
                      <tr>
                        <td>Prótesis removible</td>
                        <td>—</td>
                      </tr>
                      <tr>
                        <td>Coronas</td>
                        <td>{counts.coronas}</td>
                      </tr>
                      <tr>
                        <td>Dientes presentes</td>
                        <td>{counts.existentes}</td>
                      </tr>
                      <tr>
                        <td>Dientes ausentes</td>
                        <td>{counts.ausentes}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* ── Grilla de dientes ── */}
                {loadingOdo ? (
                  <p className="hci-odo-loading">Cargando odontograma…</p>
                ) : (
                  <div className="hci-odo-grid">
                    <div className="hci-arch-lbl">Superior permanente</div>
                    <div className="hci-odo-row-wrap">
                      <ToothRow nums={[18, 17, 16, 15, 14, 13, 12, 11]} odonto={odonto} />
                      <div className="hci-odo-sep" />
                      <ToothRow nums={[21, 22, 23, 24, 25, 26, 27, 28]} odonto={odonto} />
                    </div>
                    <div className="hci-odo-row-wrap hci-odo-temp">
                      <ToothRow nums={[55, 54, 53, 52, 51]} odonto={odonto} />
                      <div className="hci-odo-sep" />
                      <ToothRow nums={[61, 62, 63, 64, 65]} odonto={odonto} />
                    </div>
                    <div className="hci-odo-midline" />
                    <div className="hci-odo-row-wrap hci-odo-temp">
                      <ToothRow nums={[85, 84, 83, 82, 81]} odonto={odonto} />
                      <div className="hci-odo-sep" />
                      <ToothRow nums={[71, 72, 73, 74, 75]} odonto={odonto} />
                    </div>
                    <div className="hci-odo-row-wrap">
                      <ToothRow nums={[48, 47, 46, 45, 44, 43, 42, 41]} odonto={odonto} />
                      <div className="hci-odo-sep" />
                      <ToothRow nums={[31, 32, 33, 34, 35, 36, 37, 38]} odonto={odonto} />
                    </div>
                    <div className="hci-arch-lbl">Inferior permanente</div>
                    {odObs && <div className="hci-odo-obs">Obs.: {odObs}</div>}
                  </div>
                )}
              </div>

              {/* ── Diagnóstico / Plan / Observaciones ────────────────────── */}
              <div className="hci-diag-row">
                <div>
                  <div className="hci-subsec">Diagnóstico presuntivo</div>
                  <div className="hci-text-val">{f.diagnostico}</div>
                </div>
                <div>
                  <div className="hci-subsec">Plan de tratamiento &nbsp;&nbsp; fecha: ___/___/___</div>
                  <div className="hci-text-val">{f.planTratamiento}</div>
                </div>
              </div>
              <div className="hci-subsec">Observaciones</div>
              <div className="hci-text-val">{f.observaciones}</div>

              {/* ── Consentimiento + Firma ─────────────────────────────────── */}
              <div className="hci-consent">
                <p className="hci-consent-text">
                  He comprendido todas las explicaciones que se me han facilitado en el lenguaje claro y sencillo,
                  he podido realizar todas las observaciones y se me han aclarado todas las dudas; por lo que estoy
                  completamente de acuerdo con el tratamiento que se me va a realizar.
                </p>
                <p className="hci-consent-texto2">
                  El/la que suscribe&nbsp;
                  <span className="hci-val hci-val--bold">{f.consentimientoNombre}</span>
                  &nbsp;DNI N°&nbsp;<span className="hci-val">{f.consentimientoDni}</span>
                  &nbsp;con domicilio en&nbsp;<span className="hci-val">{f.consentimientoDomicilio}</span>
                  &nbsp;otorgo mi consentimiento para realizar el tratamiento necesario para rehabilitar
                  mi salud bucodental propuesta por el/la Dr/a MP&nbsp;
                  <span className="hci-val">{f.consentimientoDr}</span>
                </p>
                <div className="hci-firma-row">
                  {f.firmaBase64 ? (
                    <div className="hci-firma-box">
                      <img src={f.firmaBase64} alt="Firma del paciente" className="hci-firma-img" />
                      <div className="hci-firma-line" />
                      <div className="hci-firma-lbl">Firma del paciente o tutor</div>
                    </div>
                  ) : (
                    <div className="hci-firma-box">
                      <div className="hci-firma-space" />
                      <div className="hci-firma-line" />
                      <div className="hci-firma-lbl">Firma del paciente o tutor</div>
                    </div>
                  )}
                  <div className="hci-firma-box">
                    <div className="hci-firma-space" />
                    <div className="hci-firma-line" />
                    <div className="hci-firma-lbl">aclaración</div>
                  </div>
                  <div className="hci-firma-box">
                    <div className="hci-firma-space" />
                    <div className="hci-firma-line" />
                    <div className="hci-firma-lbl">DNI N°</div>
                  </div>
                </div>
              </div>

            </div>{/* ── fin hci-page ── */}
          </div>
        )}
      </div>
    </div>
  );
}
